import { CARRY_MODES, FORM_STAGE, SKILLS, UNIQUES, UNIQUE_FORMS, type CarryMode, type FormKey, type UniqueId, type UniqueSlot } from '@somnhold/content';
import { formBlock, loadoutFor, ownUnique, type HeroState, type Loadout } from '@somnhold/sim';
import { t } from '../i18n.ts';
import { h } from '../ui/dom.ts';
import { formArt } from './form-art.ts';

/*
 * The forms of the unique pieces on screen (docs/UNICAS.md §3, §6): in «lo que llevás», under the piece's skill (or by
 * the passive for the three pieces without a button), its two forms as cards like the variants, a «no form» card, and
 * the final form below; on the piece's sheet, its forms with the kinds of fight that carry each. Plain DOM in the
 * bastion's sheets and waiting rooms: nothing here runs in a fight.
 */

const SLOTS: readonly UniqueSlot[] = ['weapon', 'armor', 'amulet'];
const ROMAN = ['', 'I', 'II', 'III', 'IV'];

/** The pieces of its own class the hero wears, with their stage. */
export const wornUniques = (hero: HeroState): { slot: UniqueSlot; id: UniqueId; stage: number }[] =>
  SLOTS.flatMap((slot) => {
    const u = ownUnique(hero, slot);
    return u ? [{ slot, id: u.id, stage: u.stage }] : [];
  });

/** The pieces whose power hangs on this button's skill (the class's own skill of that button), or on no button (`null`). */
export const uniquesOnButton = (hero: HeroState, button: 0 | 1 | 2 | null): { slot: UniqueSlot; id: UniqueId; stage: number }[] =>
  wornUniques(hero).filter((u) => {
    const sk = UNIQUES[u.id].skill;
    return button === null ? !sk : !!sk && SKILLS[sk].slot === button;
  });

/** What clashes with a chosen form on its button, named (a variant or a glyph). */
const clashWith = (id: UniqueId, key: FormKey, l: Loadout): string => {
  const c = UNIQUE_FORMS[id][key]?.clash;
  const sk = UNIQUES[id].skill;
  const at = l.picks.findIndex((p) => p.skill === sk);
  const fork = at >= 0 ? l.picks[at]?.variant : null;
  const glyph = at >= 0 ? l.glyphs?.[at] : null;
  if (fork && c?.fork?.includes(fork)) return t(`fork.${fork}`);
  if (glyph && c?.glyph?.includes(glyph)) return t(`glyph.${glyph}`);
  return '';
};

/** A form's card: drawing, name, what changes, better in green and worse in red, and when it pays. */
const formCard = (id: string, on: boolean, extra: { attrs: Record<string, string | boolean>; onclick?: () => void; disabled?: boolean; cls?: string; note?: string }): HTMLElement => {
  const down = t(`uform.${id}.down`);
  return h(
    `button.uform-card${on ? '.on' : ''}${extra.cls ?? ''}`,
    { ...extra.attrs, 'aria-pressed': String(on), disabled: !!extra.disabled, ...(extra.onclick ? { onclick: extra.onclick } : {}) },
    h('span.uform-art', { html: formArt(id) }),
    h(
      'span.uform-txt',
      {},
      h('b.uform-name', {}, t(`uform.${id}`), on ? h('span.uform-on', {}, t('uform.carried')) : ''),
      h('span.uform-desc', {}, t(`uform.${id}.desc`)),
      h('span.uform-diff', {}, h('span.up', {}, `▲ ${t(`uform.${id}.up`)}`), down !== `uform.${id}.down` ? h('span.down', {}, `▼ ${down}`) : ''),
      h('span.uform-when', {}, t('tree.when', { w: t(`uform.${id}.when`) })),
      extra.note ? h('span.uform-note', {}, extra.note) : '',
    ),
  );
};

/** The final form: lit from the IV, grey before; it adds to whichever form is chosen. */
const finalCard = (id: UniqueId, stage: number, blocked: boolean): HTMLElement => {
  const fin = UNIQUE_FORMS[id].final.id;
  const lit = stage >= FORM_STAGE.final;
  return h(
    `div.uform-final${lit ? '.lit' : ''}${blocked ? '.off' : ''}`,
    { 'data-test': 'uform-final', 'data-form': fin },
    h('span.uform-art', { html: formArt(fin) }),
    h(
      'span.uform-txt',
      {},
      h('span.uform-cap', {}, `${t('uform.final')} · IV`),
      h('b.uform-name', {}, t(`uform.${fin}`)),
      h('span.uform-desc', {}, t(`uform.${fin}.desc`)),
      h('span.uform-when', {}, lit ? t('uform.final.plus') : t('uform.finalLocked')),
    ),
  );
};

/**
 * One piece's forms in «lo que llevás»: the two cards and «no form» (tapping saves the preparation), why they don't go
 * when the piece's skill isn't carried or the button already does the same, and its final form. `save` gets the
 * preparation with that slot's form set (or cleared); `over`: the campaign is done (nothing changes).
 */
export const formPicker = (hero: HeroState, u: { slot: UniqueSlot; id: UniqueId; stage: number }, l: Loadout, save: (next: Loadout) => void, over: boolean): HTMLElement => {
  const { slot, id, stage } = u;
  const box = h('section.uform', { 'data-test': 'uform', 'data-unique': id });
  box.append(
    h(
      'div.uform-head',
      {},
      h('span.uform-gem', { 'aria-hidden': 'true' }, h('span', {}, ROMAN[stage] || '·')),
      h('span.uform-title', {}, h('span.uform-cap', {}, t('uform.title')), h('b', {}, t(`unique.${id}`))),
    ),
  );
  if (stage < FORM_STAGE.choose) {
    box.append(h('p.uform-locked', { 'data-test': 'uform-locked' }, t('uform.locked')), finalCard(id, stage, false));
    return box;
  }
  const want = l.forms?.[slot] ?? null;
  const block = formBlock(id, l, 'final');
  if (block === 'noSkill') {
    const swarm = id === 'anvilHarness' && l.picks.some((p) => p.skill === 'automaton' && p.variant === 'swarm');
    box.append(h('p.uform-warn', { 'data-test': 'uform-noskill' }, swarm ? t('uform.swarm') : t('uform.noSkill', { skill: t(`skill.${UNIQUES[id].skill}`) })));
  }
  const pick = (k: FormKey | null): Loadout => {
    const forms = { ...(l.forms ?? {}) };
    if (k) forms[slot] = k;
    else delete forms[slot];
    return { ...l, forms };
  };
  const none = h(
    `button.uform-card.none${want === null ? '.on' : ''}`,
    { 'data-form': 'none', 'aria-pressed': String(want === null), disabled: over || want === null, onclick: () => save(pick(null)) },
    h('span.uform-art', { html: formArt('') }),
    h('span.uform-txt', {}, h('b.uform-name', {}, t('uform.none'), want === null ? h('span.uform-on', {}, t('uform.carried')) : ''), h('span.uform-desc', {}, t('uform.none.desc'))),
  );
  // A panoply piece has no forms of the II (docs/PANOPLIA.md): only its final one.
  const cards = (['a', 'b'] as const).flatMap((k) => {
    const def = UNIQUE_FORMS[id][k];
    if (!def) return [];
    const on = want === k;
    const clash = formBlock(id, l, k) === 'clash';
    return formCard(def.id, on, {
      attrs: { 'data-form': def.id, 'data-key': k },
      disabled: over || on,
      onclick: () => save(pick(k)),
      cls: `${clash ? '.clash' : ''}${block === 'noSkill' ? '.idle' : ''}`,
      ...(clash ? { note: t('uform.clash', { what: clashWith(id, k, l) }) } : {}),
    });
  });
  box.append(h('div.uform-cards', { role: 'radiogroup', 'aria-label': t('uform.pick') }, ...cards, none), finalCard(id, stage, block === 'noSkill'));
  return box;
};

/** The modes that carry each form of a piece (for its sheet). */
const modesWith = (hero: HeroState, slot: UniqueSlot): Record<FormKey | 'none', CarryMode[]> => {
  const out: Record<FormKey | 'none', CarryMode[]> = { a: [], b: [], none: [] };
  for (const m of CARRY_MODES) out[loadoutFor(hero, m).forms?.[slot] ?? 'none'].push(m);
  return out;
};

/** A piece's forms on its sheet: the two (each with the kinds of fight that carry it) and the final one. */
export const formSheet = (hero: HeroState, slot: UniqueSlot, id: UniqueId, stage: number): HTMLElement => {
  const box = h('section.uform.on-piece', { 'data-test': 'uform-sheet', 'data-unique': id }, h('div.uform-cap.uform-sheet-cap', {}, t('uform.title')));
  if (stage < FORM_STAGE.choose && UNIQUE_FORMS[id].a) box.append(h('p.uform-locked', {}, t('uform.locked')));
  const modes = stage >= FORM_STAGE.choose ? modesWith(hero, slot) : { a: [], b: [], none: [] };
  const row = (k: FormKey): HTMLElement => {
    const fid = UNIQUE_FORMS[id][k]?.id ?? '';
    const ms = modes[k];
    return h(
      `div.uform-row${ms.length ? '.on' : ''}${stage < FORM_STAGE.choose ? '.dim' : ''}`,
      { 'data-form': fid },
      h('span.uform-art', { html: formArt(fid) }),
      h(
        'span.uform-txt',
        {},
        h('b.uform-name', {}, t(`uform.${fid}`)),
        h('span.uform-desc', {}, t(`uform.${fid}.desc`)),
        ms.length ? h('span.uform-modes', {}, ...ms.map((m) => h('i', {}, t(`carry.mode.${m}`)))) : '',
      ),
    );
  };
  const two = UNIQUE_FORMS[id].a && UNIQUE_FORMS[id].b ? [h('div.uform-rows', {}, row('a'), row('b'))] : [];
  box.append(...two, finalCard(id, stage, false));
  return box;
};
